import type { ContentRegistry } from '../content/registry';
import { characterDef } from '../content/characters';
import { PETS } from '../content/pets';
import type { SaveData } from '../meta/save';
import type { RosterEntry } from '../net/protocol';
import type { CoopSession } from '../net/session';
import { MAX_COOP_PLAYERS } from '../net/coopConfig';
import { clear, h } from './dom';
import { availableStartEras } from './menuModel';
import { paradoxDef, paradoxLabel } from '../content/paradox';

/** Online co-op screens: entry (host / join), lobby, and in-run waiting banners. */

export interface OnlineEntryActions {
  /** 'ready' = can host/join; 'signin' = cloud build, not signed in; 'offline' = no online service in this build. */
  availability: 'ready' | 'signin' | 'offline';
  local: boolean;
  name: string;
  /** Prefilled join code (the last one used in this tab — makes rejoining one click). */
  code?: string;
  message?: string;
  host(name: string): void;
  join(code: string, name: string): void;
  signIn(kind: 'google' | 'anon'): void;
  back(): void;
}

export function onlineEntryScreen(a: OnlineEntryActions): HTMLElement {
  const status = h('div', { class: 'coop-status sub', 'aria-live': 'polite' }, a.message ?? '');
  const nameInput = h('input', { class: 'coop-input', type: 'text', maxlength: 24, value: a.name, placeholder: 'Your name', 'aria-label': 'Your name' });
  const codeInput = h('input', { class: 'coop-input coop-code-input', type: 'text', maxlength: 7, placeholder: 'CODE', autocomplete: 'off', spellcheck: 'false', 'aria-label': 'Lobby code', value: a.code ?? '' });
  const busy = (text: string) => {
    status.textContent = text;
    root.querySelectorAll<HTMLButtonElement>('button[data-act]').forEach((b) => (b.disabled = true));
  };
  const name = () => nameInput.value.trim() || 'Traveler';
  const doJoin = () => {
    const code = codeInput.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (code.length < 4) {
      status.textContent = 'Enter the code the host shared with you.';
      codeInput.focus();
      return;
    }
    busy('Joining…');
    a.join(code, name());
  };
  codeInput.addEventListener('keydown', (e) => {
    if ((e as KeyboardEvent).key === 'Enter') doJoin();
  });
  codeInput.addEventListener('input', () => (codeInput.value = codeInput.value.toUpperCase()));

  const ready = a.availability === 'ready';
  const body = a.availability === 'offline'
    ? h('div', { class: 'col' }, h('p', { class: 'sub' }, 'Online co-op needs the cloud backend, which this build is not configured for. Local co-op (Party on the main menu) still works.'), h('p', { class: 'sub' }, 'Developers: add ?coop=local to the URL to test online co-op between two windows of this browser.'))
    : a.availability === 'signin'
      ? h('div', { class: 'col' }, h('p', { class: 'sub' }, 'Playing online needs a cloud identity so the lobby service can pair you. A guest account is fine and keeps your progress local-first.'), h('div', { class: 'row' }, h('button', { class: 'primary', onClick: () => a.signIn('anon') }, 'Continue as guest'), h('button', { onClick: () => a.signIn('google') }, 'Sign in with Google')))
      : null;

  const root = h('div', { class: 'screen coop-screen' },
    h('div', { class: 'panel col coop-panel' },
      h('header', { class: 'run-setup-heading' },
        h('div', null, h('div', { class: 'character-kicker' }, a.local ? 'Online co-op · local test mode' : 'Online co-op'), h('h2', null, 'Fall through time together')),
        h('button', { class: 'small', onClick: a.back }, 'Back'),
      ),
      h('p', { class: 'sub' }, `Up to ${MAX_COOP_PLAYERS} players. Everyone brings their own timewalker, equipment and pet, and keeps their own rewards.`),
      body,
      ready
        ? h('div', { class: 'coop-entry-grid' },
            h('label', { class: 'col coop-field' }, h('small', null, 'Name'), nameInput),
            h('section', { class: 'coop-card col' },
              h('h3', null, 'Host a game'),
              h('p', { class: 'sub' }, 'Get a code to share. You choose the starting point and difficulty.'),
              h('button', { class: 'primary', 'data-act': 'host', 'data-default-focus': true, onClick: () => { busy('Opening a lobby…'); a.host(name()); } }, 'Host a game'),
            ),
            h('section', { class: 'coop-card col' },
              h('h3', null, 'Join a game'),
              h('p', { class: 'sub' }, 'Enter the code from your host. Dropped out mid-run? Join the same code again to take your character back.'),
              h('div', { class: 'row' }, codeInput, h('button', { class: 'primary', 'data-act': 'join', onClick: doJoin }, 'Join')),
            ),
          )
        : null,
      status,
    ),
  );
  return root;
}

export interface LobbyActions {
  start(): void;
  setReady(ready: boolean): void;
  setSettings(startEra: number, paradox: number): void;
  editLoadout(): void;
  leave(): void;
}

function rosterCard(reg: ContentRegistry, entry: RosterEntry | null, index: number, you: boolean): HTMLElement {
  if (!entry) return h('div', { class: 'coop-slot empty' }, h('span', { class: 'coop-slot-num' }, `P${index + 1}`), h('span', { class: 'sub' }, 'Open slot'));
  const character = characterDef(entry.character);
  const weapon = reg.items.get(entry.weapon)?.name ?? 'Spore Launcher';
  const pet = PETS.find((p) => p.id === entry.pet)?.name ?? 'No pet';
  const state = entry.host ? 'Host' : entry.ready ? 'Ready' : 'Not ready';
  return h('div', { class: `coop-slot${entry.ready ? ' ready' : ''}${you ? ' you' : ''}` },
    h('span', { class: 'coop-slot-num' }, `P${index + 1}`),
    h('div', { class: 'col coop-slot-body' },
      h('strong', null, entry.name, you ? h('em', null, ' (you)') : null),
      h('span', { class: 'sub' }, `${character.name} · ${weapon} · ${pet}`),
    ),
    h('div', { class: 'col coop-slot-state' }, h('span', { class: `coop-pill ${entry.host ? 'host' : entry.ready ? 'ok' : ''}` }, state), entry.ping !== null ? h('small', { class: 'sub' }, `${entry.ping} ms`) : null),
  );
}

/** Lobby screen; call `refresh()` whenever the session reports a change. */
export function lobbyScreen(session: CoopSession, save: SaveData, reg: ContentRegistry, a: LobbyActions): { root: HTMLElement; refresh(): void } {
  const isHost = session.role === 'host';
  const codeEl = h('div', { class: 'coop-code' });
  const roster = h('div', { class: 'coop-roster' });
  const settings = h('div', { class: 'menu-run-setup coop-settings' });
  const actions = h('div', { class: 'run-setup-actions' });
  const note = h('div', { class: 'coop-status sub', 'aria-live': 'polite' });
  const starts = availableStartEras(save, reg);
  let lastControlsKey = '';

  const refresh = () => {
    const entries = session.role === 'host' ? session.roster() : session.rosterEntries;
    const set = session.settings;
    const myId = session.role === 'host' ? 'host' : session.myId;
    codeEl.textContent = session.role === 'host' ? session.code ?? '······' : '';
    clear(roster);
    for (let i = 0; i < MAX_COOP_PLAYERS; i++) roster.appendChild(rosterCard(reg, entries[i] ?? null, i, entries[i]?.id === myId));

    // Controls are rebuilt only when something they show changes — the roster
    // refreshes every second for pings, and rebuilding buttons that often would
    // eat clicks and gamepad focus.
    const controlsKey = JSON.stringify([
      entries.map((e) => [e.id, e.ready]),
      set,
      session.role === 'host' ? session.canStart() : [session.ready, session.hostInLobby],
    ]);
    if (controlsKey === lastControlsKey) return;
    lastControlsKey = controlsKey;

    clear(settings);
    const eraName = reg.eras[set.startEra]?.name ?? 'Full timeline';
    const cycle = (values: number[], current: number) => values[(Math.max(0, values.indexOf(current)) + 1) % values.length];
    settings.append(
      h('button', { disabled: !isHost, onClick: () => a.setSettings(cycle(starts, set.startEra), set.paradox) }, h('small', null, 'Starting point'), h('strong', null, set.startEra === 0 ? 'Full timeline' : eraName), isHost ? h('span', null, '↻') : null),
      h('button', { disabled: !isHost, title: paradoxDef(set.paradox).description, onClick: () => a.setSettings(set.startEra, (set.paradox + 1) % (save.paradoxUnlocked + 1)) }, h('small', null, 'Difficulty'), h('strong', null, paradoxLabel(set.paradox)), isHost ? h('span', null, '↻') : null),
      h('button', { disabled: true }, h('small', null, 'Party'), h('strong', null, `${entries.length} / ${MAX_COOP_PLAYERS}`)),
    );

    clear(actions);
    actions.append(h('button', { class: 'small', onClick: a.leave }, isHost ? 'Close lobby' : 'Leave'), h('button', { class: 'small', onClick: a.editLoadout }, 'Change loadout'));
    if (session.role === 'host') {
      const can = session.canStart();
      actions.append(h('button', { class: 'primary run-confirm', disabled: !can, 'data-default-focus': true, onClick: a.start }, 'Start run', h('b', { 'aria-hidden': 'true' }, '→')));
      note.textContent = entries.length < 2 ? 'Share the code — the run can start once someone joins.' : can ? 'Everyone is ready.' : 'Waiting for everyone to press Ready…';
    } else {
      const waitingHost = !session.hostInLobby;
      actions.append(h('button', { class: session.ready ? 'run-confirm' : 'primary run-confirm', disabled: waitingHost, 'data-default-focus': true, onClick: () => a.setReady(!session.ready) }, session.ready ? 'Not ready' : 'Ready'));
      note.textContent = waitingHost ? 'Waiting for the host to return to the lobby…' : session.ready ? 'Waiting for the host to start the run…' : 'Press Ready when your loadout is set.';
    }
  };

  const root = h('div', { class: 'screen coop-screen' },
    h('div', { class: 'panel col coop-panel' },
      h('header', { class: 'run-setup-heading' },
        h('div', null, h('div', { class: 'character-kicker' }, isHost ? 'Your lobby' : 'Lobby'), h('h2', null, isHost ? 'Invite your party' : 'Waiting in the rift')),
        isHost
          ? h('div', { class: 'col coop-code-wrap' }, h('small', { class: 'sub' }, 'Lobby code'), codeEl, h('button', { class: 'small', onClick: () => void navigator.clipboard?.writeText(codeEl.textContent ?? '').then(() => (note.textContent = 'Code copied.')).catch(() => undefined) }, 'Copy code'))
          : null,
      ),
      roster,
      settings,
      note,
      actions,
    ),
  );
  refresh();
  return { root, refresh };
}

/** Small non-blocking banner shown while the party waits on someone else. */
export function waitingBanner(text: string): HTMLElement {
  return h('div', { class: 'coop-waiting' }, h('span', { class: 'coop-spinner', 'aria-hidden': 'true' }), h('span', null, text));
}
